import { useEffect, useState } from 'react';
import { Loader2, Send } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/lib/toast';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { useVisualViewportInset } from '@/hooks/useVisualViewportInset';
import { useEmailField } from '@/hooks/useEmailField';
import { cn } from '@/lib/utils';

export interface StorefrontContactItemContext {
	id?: string;
	title: string;
	imageUrl?: string;
	priceCents?: number | null;
	url?: string;
	conditionLabel?: string;
	setName?: string;
}

interface StorefrontContactSheetProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** user_id of the storefront owner who should receive the message. */
	sellerUserId: string;
	/** Display name used in header copy ("Message Clint"). */
	sellerDisplayName: string;
	/** Optional prefilled message body (e.g. item context from a listing page). */
	initialMessage?: string;
	/** Optional item context — renders a photo + details card in the seller's email. */
	itemContext?: StorefrontContactItemContext;
	/** When true, shows a dedicated required "Your offer" numeric field. */
	offerMode?: boolean;
}


/**
 * Public "Message the storefront owner" modal.
 *
 * No auth required — buyers on a public storefront can submit a message that
 * gets emailed to the owner's account email and persisted in
 * `storefront_inquiries`. Templated by `sellerUserId` so every future
 * multi-tenant storefront can drop in the same component.
 */
export function StorefrontContactSheet({
	open,
	onOpenChange,
	sellerUserId,
	sellerDisplayName,
	initialMessage,
	itemContext,
	offerMode = false,
}: StorefrontContactSheetProps) {
	const isMobile = useIsMobile();
	const { keyboardHeight } = useVisualViewportInset();
	const [name, setName] = useState('');
	const email = useEmailField('', { requireReplyable: true });
	const [socials, setSocials] = useState('');
	const [message, setMessage] = useState(initialMessage ?? '');
	const [offerInput, setOfferInput] = useState('');
	const [website, setWebsite] = useState(''); // honeypot
	const [busy, setBusy] = useState(false);

	const parsedOffer = (() => {
		const cleaned = offerInput.replace(/[^0-9.]/g, '');
		if (!cleaned) return null;
		const n = Number.parseFloat(cleaned);
		return Number.isFinite(n) && n > 0 ? n : null;
	})();
	const offerValid = !offerMode || parsedOffer !== null;

	useEffect(() => {
		if (open && initialMessage) {
			setMessage((prev) => (prev ? prev : initialMessage));
		}
	}, [open, initialMessage]);

	useEffect(() => {
		if (!open) {
			// reset only after close animation
			const t = setTimeout(() => {
				setName('');
				email.reset();
				setSocials('');
				setMessage(initialMessage ?? '');
				setOfferInput('');
				setWebsite('');
				setBusy(false);
			}, 300);
			return () => clearTimeout(t);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, initialMessage]);

	const submit = async () => {
		if (!name.trim() || !message.trim()) {
			toast.error('Please fill out every field.');
			return;
		}
		if (offerMode && parsedOffer === null) {
			toast.error('Enter your offer amount.');
			return;
		}
		if (!email.validate()) {
			toast.error(email.error ?? 'Please enter a valid email address.');
			return;
		}
		setBusy(true);
		try {
			const { data, error } = await supabase.functions.invoke('send-storefront-contact', {
				body: {
					sellerUserId,
					name: name.trim(),
					email: email.normalized,
					message: message.trim(),
					socials: socials.trim() || undefined,
					pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
					itemContext: itemContext ?? undefined,
					offerAmount: parsedOffer ?? undefined,
					website,
				},
			});
			if (error || (data as any)?.error) {
				const msg = (data as any)?.error ?? error?.message ?? 'Please try again.';
				toast.error('Could not send message', { description: msg });
				return;
			}
			toast.forceSuccess(`Message sent to ${sellerDisplayName}`, {
				description: 'They\'ll reply to your email directly.',
			});
			onOpenChange(false);
		} catch (err: any) {
			toast.error('Could not send message', {
				description: err?.message ?? 'Please try again.',
			});
		} finally {
			setBusy(false);
		}
	};


	const body = (
		<div className="flex flex-col gap-4 pb-6 sm:pb-0">
			{/* Honeypot */}
			<input
				type="text"
				name="website"
				value={website}
				onChange={(e) => setWebsite(e.target.value)}
				tabIndex={-1}
				autoComplete="off"
				className="hidden"
				aria-hidden="true"
			/>

			<Input
				id="sc-name"
				value={name}
				onChange={(e) => setName(e.target.value)}
				placeholder="Your name"
				maxLength={100}
				autoComplete="name"
				aria-label="Your name"
			/>

			<div>
				<Input
					id="sc-email"
					type="email"
					inputMode="email"
					value={email.value}
					onChange={(e) => email.setValue(e.target.value)}
					onBlur={email.onBlur}
					placeholder="Your email"
					maxLength={255}
					autoComplete="email"
					aria-label="Your email"
					aria-invalid={email.showError || undefined}
					className={cn(email.showError && 'border-destructive focus-visible:ring-destructive')}
				/>
				{email.showError && (
					<p className="mt-1 text-[11px] text-destructive">{email.error}</p>
				)}
			</div>

			<Textarea
				id="sc-socials"
				value={socials}
				onChange={(e) => setSocials(e.target.value)}
				placeholder="Your social @ handles, phone number, etc. (optional)"
				rows={2}
				maxLength={300}
				autoComplete="off"
				aria-label="Your social handles"
				className="resize-none"
			/>

			{offerMode && (
				<div>
					<div className="relative">
						<span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
						<Input
							id="sc-offer"
							type="text"
							inputMode="decimal"
							value={offerInput}
							onChange={(e) => setOfferInput(e.target.value.replace(/[^0-9.]/g, ''))}
							placeholder="Your offer"
							aria-label="Your offer in USD"
							className="pl-6"
						/>
					</div>
					
				</div>
			)}

			<Textarea
				id="sc-message"
				value={message}
				onChange={(e) => setMessage(e.target.value)}
				placeholder={offerMode ? 'Any notes for the seller? (shipping, trade-ins, etc.)' : `Hey ${sellerDisplayName}, I'm interested in…`}
				rows={5}
				maxLength={4000}
				aria-label="Message"
			/>

			<Button
				onClick={submit}
				disabled={busy || !name.trim() || !message.trim() || !email.isValid || !offerValid}
				className="w-full h-11"
			>
				{busy ? (
					<>
						<Loader2 className="mr-2 h-4 w-4 animate-spin" />
						Sending…
					</>
				) : (
					<>{offerMode ? 'Send Offer' : 'Send Message'}</>
				)}
			</Button>


		</div>
	);

	if (isMobile) {
		return (
			<Sheet open={open} onOpenChange={onOpenChange}>
				<SheetContent
					side="bottom"
					className="max-h-[92dvh] flex flex-col"
					style={{ paddingBottom: `calc(env(safe-area-inset-bottom) + 16px + ${keyboardHeight}px)` }}
				>
					<SheetHeader className="text-left">
						<SheetTitle>Message {sellerDisplayName}</SheetTitle>
						<SheetDescription className="sr-only">
							{offerMode
								? `Send an offer to ${sellerDisplayName}. They'll reply to your email directly.`
								: `Send a message to ${sellerDisplayName}. They'll reply to your email directly.`}
						</SheetDescription>
					</SheetHeader>
					<div className="mt-4 overflow-y-auto">{body}</div>
				</SheetContent>
			</Sheet>
		);
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:w-[560px] sm:max-w-[calc(100vw-2rem)]">
				<DialogHeader>
					<DialogTitle>Message {sellerDisplayName}</DialogTitle>
					<DialogDescription className="sr-only">
						{offerMode
							? `Send an offer to ${sellerDisplayName}. They'll reply to your email directly.`
							: `Send a message to ${sellerDisplayName}. They'll reply to your email directly.`}
					</DialogDescription>
				</DialogHeader>
				{body}
			</DialogContent>
		</Dialog>
	);
}

export default StorefrontContactSheet;
